import 'react-phone-input-2/lib/material.css'

import { FC } from 'react'
import {
  Box,
  Button,
  DatePicker,
  FormHelperText,
  FormLabel,
  makeStyles,
  MuiStack,
  TextField,
  Typography,
} from '@perk-ui/core'
import { AxiosError } from 'axios'
import { endOfYesterday, format, getYear, isPast, isToday } from 'date-fns'
import { useFormik } from 'formik'
import PhoneInput, { CountryData } from 'react-phone-input-2'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'

import { useSitesContext } from '../../../components/SitesProvider'
import useInviteOnlyPatient from '../../../features/query-hooks/useInviteOnlyPatient'
import useDates from '../../../hooks/useDates'
import PatientSelfOnboardUrls from '../PatientSelfOnboardUrls'

const useStyles = makeStyles((theme) => ({
  title: {
    fontFamily: 'DM Sans',
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: '24px',
    textAlign: 'center',
    marginBottom: '40px',
  },
  inputStack: {
    '& .MuiTextField-root': {
      marginBottom: '14px',
    },
  },
  goBackButton: {
    border: '1px solid #90CAF9', // Light blue border
    borderRadius: '8px', // Rounded corners
    color: '#42A5F5 !important', // Blue text color
    textColor: '#42A5F5', // Blue text color
    textTransform: 'none', // Prevents all-uppercase text
    // fontWeight: 500, // Semi-bold text
    backgroundColor: 'white', // White background
    '&:hover': {
      backgroundColor: '#E3F2FD', // Light blue background on hover
    },
  },
  phoneFieldContainer: {
    marginBottom: '14px',
    '& .special-label': {
      display: 'none',
    },
    '& .flag-dropdown': {
      marginTop: theme.spacing(1),
    },
    '& input.form-control': {
      width: '100%',
      border: '2px solid transparent',
      backgroundColor: '#f5f5f8',
      borderRadius: theme.spacing(1),
      marginTop: theme.spacing(1),
      '&:focus': {
        outline: 0,
        boxShadow: 'none',
        borderColor: '#000',
      },
      '&:hover': {
        borderColor: '#000',
      },
    },
  },
  phoneLabel: {},
}))

interface PatientForm {
  firstName: string
  lastName: string
  cellPhoneLocal: string
  cellPhoneCountryCode: string
  dateOfBirth: string | Date
}

interface PatientProps {}

type PatientDateString = Omit<PatientForm, 'dateOfBirth'> & {
  dateOfBirth: string
}

const Patient: FC<PatientProps> = () => {
  const inviteOnlyPatient = useInviteOnlyPatient()
  const classes = useStyles()
  const { localizedParseDate, localizedDatePattern } = useDates()

  const { currentSite } = useSitesContext()

  const { push, goBack } = useHistory()

  const country = currentSite?.country
  const isUsa = country === 'united_states_of_america'

  const patientValidations = yup.object({
    firstName: yup.string().defined('First name is required'),
    lastName: yup.string().defined('Last name is required'),
    dateOfBirth: yup
      .string()
      .defined('Birthdate is required')
      .test(
        'is-valid-date',
        `Date of birth must be in format ${localizedDatePattern.toLowerCase()} and a valid date`,
        (value = '') => !!localizedParseDate(value),
      )
      .test(
        'is-before-now',
        'Date of birth must be before today',
        (value = '') => {
          const asDate = localizedParseDate(value)
          return asDate && isPast(asDate) && !isToday(asDate)
        },
      )
      .test('is-after-year-1900', 'Year must be after 1900', (value = '') => {
        const asDate = localizedParseDate(value)
        return asDate && getYear(asDate) >= 1900
      }),
    cellPhoneLocal: yup
      .string()
      .transform((value) => value.replace(/[()-\s]/g, '')) // strip parenthesis, space, and dash
      .length(10, 'Cell phone should be 10 numbers')
      .matches(/^[0-9]{10}$/, 'Please check the cell phone')
      .defined('Cell phone is required to send assessments'),
  })

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
    setFieldError,
    isSubmitting,
    isValid,
    submitForm,
  } = useFormik<PatientForm>({
    initialValues: {
      firstName: '',
      lastName: '',
      cellPhoneLocal: '',
      cellPhoneCountryCode: country === 'united_kingdom' ? '44' : '1',
      dateOfBirth: '',
    },
    validationSchema: patientValidations,
    onSubmit: (values) => {
      const formattedDates = {} as Partial<PatientDateString>
      // Remove () and -
      values.cellPhoneLocal = values.cellPhoneLocal.replace(/[()-\s]/g, '')
      // Format to mm/dd/yyyy
      if (values.dateOfBirth instanceof Date) {
        formattedDates.dateOfBirth = format(values.dateOfBirth, 'yyyy-MM-dd')
      } else {
        const dob = localizedParseDate(values.dateOfBirth)

        if (!dob) return
        formattedDates.dateOfBirth = format(dob, 'yyyy-MM-dd')
      }
      return inviteOnlyPatient
        .mutateAsync({
          ...values,
          ...formattedDates,
        })
        .then((result) => {
          if (result.patient.id) {
            push(PatientSelfOnboardUrls.addCase.create(result.patient.id))
          }
        })
        .catch((err) => {
          if (err?.isAxiosError) {
            const error = err as AxiosError

            if (error.response?.status === 422) {
              const data = error.response.data
              const phoneString = data?.errors?.cellPhoneLocal?.[0]
              const isPhoneTaken =
                phoneString?.includes('has already been taken') ||
                phoneString?.includes(
                  'is already in use by another active patient at this site.',
                )
              isPhoneTaken &&
                setFieldError('cellPhoneLocal', 'This number is already in use')
            }
          }
        })
    },
  })

  return (
    <>
      <Box
        justifyContent="space-between"
        sx={{
          maxWidth: '787px',
          width: '100%',
          height: '100%',
        }}
      >
        <MuiStack justifyContent="center" className={classes.inputStack}>
          <Typography className={classes.title}>
            Enter patient details
          </Typography>
          <TextField
            id="patient-verify-first-name"
            label="First Name"
            name="firstName"
            required
            fullWidth
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.firstName && Boolean(errors.firstName)}
            helperText={touched.firstName && errors.firstName}
          />
          <TextField
            id="patient-verify-last-name"
            label="Last Name"
            name="lastName"
            required
            fullWidth
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.lastName && Boolean(errors.lastName)}
            helperText={touched.lastName && errors.lastName}
          />
          <DatePicker
            id="patient-verify-date-of-birth"
            label="Date of Birth"
            name="dateOfBirth"
            // defaultDate={isUsa ? '01/31/1990' : '31/01/1990'}
            inputFormat={isUsa ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
            required
            fullWidth
            maxDate={endOfYesterday()}
            // isEditing={values.dateOfBirth ? values.dateOfBirth : undefined}
            setFieldValue={setFieldValue}
            onBlur={handleBlur}
            error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
            helperText={touched.dateOfBirth && errors.dateOfBirth}
          />
          <div className={classes.phoneFieldContainer}>
            <FormLabel className={classes.phoneLabel} required>
              Cell Phone
            </FormLabel>
            <PhoneInput
              inputProps={{
                name: 'cellPhoneLocal',
                required: true,
                id: 'patient-verify-cell-phone',
              }}
              containerClass="MuiInput-root MuiInputBase-root MuiInputBase-colorPrimary MuiInputBase-fullWidth MuiInputBase-formControl"
              inputClass="MuiInput-input MuiInputBase-input"
              onlyCountries={['us', 'ca', 'gb']}
              preserveOrder={['onlyCountries']}
              masks={{ ve: '(...) ...-....' }}
              country={
                country === 'canada'
                  ? 'ca'
                  : country === 'united_kingdom'
                  ? 'gb'
                  : 'us'
              }
              value={values.cellPhoneCountryCode + values.cellPhoneLocal}
              onBlur={handleBlur}
              onChange={(value, info) => {
                const data = info as CountryData
                const phoneNumber = value.replace(data?.dialCode, '')
                setFieldValue('cellPhoneCountryCode', data?.dialCode || '')
                setFieldValue('cellPhoneLocal', phoneNumber || '')
              }}
            />
            {touched?.cellPhoneLocal &&
              typeof errors !== 'string' &&
              Boolean(errors?.cellPhoneLocal) && (
                <FormHelperText error>
                  {touched?.cellPhoneLocal &&
                    typeof errors !== 'string' &&
                    errors?.cellPhoneLocal}
                </FormHelperText>
              )}
          </div>
        </MuiStack>
        <MuiStack
          direction="row"
          justifyContent="center"
          sx={{
            marginTop: '40px',
            marginBottom: '40px',
            paddingBottom: '40px',
          }}
        >
          <Button
            className={classes.goBackButton}
            disableElevation
            fullWidth
            style={{ marginRight: '20px' }}
            onClick={goBack}
          >
            Go Back
          </Button>
          {/* TODO: change submit form to validation and change step */}
          <Button
            variant="contained"
            disableElevation
            fullWidth
            loading={isSubmitting}
            disabled={!isValid}
            onClick={() => {
              submitForm()
            }}
          >
            Continue
          </Button>
        </MuiStack>
      </Box>
    </>
  )
}

export default Patient
